import { UpdateContactForm } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/Components/JourneyActionsPanel/NodeEditorPanel/Components/UpdateContactFormItems/constants";
import { getLogger } from "Services/LoggingService";
import { Elements, FlowElement, JourneyStatus, NodeTriggerType, NodeType } from "Types/Journey";

import {
    EightByEightFormItemKeys,
    VoicemailFormItemKeys,
    AgentlessCallFormItemKeys,
    VoiceAIFormItemKeys,
    GenesysFormItemKeys,
    TalkdeskFormItemKeys,
} from "../Components/JourneyBuilder/Components/JourneyActionsPanel/NodeEditorPanel/Components/_DemoFormItems/constants";
import { operatorRequiresPropertyValue } from "../Components/JourneyBuilder/Components/JourneyActionsPanel/NodeEditorPanel/Components/ConditionalFormItems/components/helpers";

const logger = getLogger("Journey Validator");

export function nodeIsValid(node: FlowElement): boolean {
    const { data, type } = node;

    switch (type) {
        case NodeType.Trigger: {
            return validateTrigger(node);
        }
        case NodeType.Call:
        case NodeType.AgentSMSTask:
        case NodeType.SMS: {
            // Note fallthrought of Call -> SMS
            return !!data?.campaign?.id;
        }
        case NodeType.Delay: {
            return validateDelay(node);
        }
        case NodeType.CancelTask: {
            const { cancelType } = data;
            return !!cancelType;
        }
        case NodeType.Conditional: {
            return validateConditional(node);
        }
        case NodeType.ABTest: {
            // Will add better validations in follow up PR with validations
            const { testName } = data || {};

            return !!testName;
        }
        case NodeType.CustomTask: {
            return !!data?.task?.name && !!data?.task?.description;
        }
        case NodeType.DEMO_EightByEight: {
            const { [EightByEightFormItemKeys.campaignId]: campaignId } = node.data || {};
            return !!campaignId;
        }
        case NodeType.DEMO_RinglessVM: {
            const { [VoicemailFormItemKeys.voicemailId]: voicemailId } = data || {};
            return !!voicemailId;
        }

        case NodeType.DEMO_AgentlessCall: {
            const { [AgentlessCallFormItemKeys.ivrId]: ivrId } = data || {};
            return !!ivrId;
        }

        case NodeType.DEMO_VoiceAI: {
            const { [VoiceAIFormItemKeys.voiceAIID]: voiceAIID } = data || {};
            return !!voiceAIID;
        }

        case NodeType.DEMO_Genesys: {
            const { [GenesysFormItemKeys.genesysID]: genesysID } = data || {};
            return !!genesysID;
        }

        case NodeType.DEMO_Talkdesk: {
            const { [TalkdeskFormItemKeys.talkdeskID]: talkdeskID } = data || {};
            return !!talkdeskID;
        }

        case NodeType.Webhook: {
            return validateWebHookNode(node);
        }
        case NodeType.UpdateContact: {
            const {
                [UpdateContactForm.eventName]: eventName,
                [UpdateContactForm.profileListName]: profileAttributesList,
            } = data || {};
            const isNodeConfigured = !!eventName || !!profileAttributesList;
            return isNodeConfigured;
        }
        // fallthroughs
        case NodeType.ScheduledCallback:
        case NodeType.AssignTargetAgent:
        case NodeType.UpdateTask:
        case NodeType.Reminder:
        case NodeType.DecisionAI: {
            return true;
        }
        default: {
            return false;
        }
    }
}

function validateTrigger(node: FlowElement): boolean {
    if (node.data?.triggerType && node.data?.triggerFiltersEvents) {
        logger.error("Could not set agent to be available because necessary data was missing", {
            triggerNode: node.data,
        });
        return false;
    }
    const eventType = node.data?.triggerType ?? node.data?.triggerFiltersEvents?.[0]?.conditions[0].conditionalType;
    const eventName = node.data?.eventName ?? node.data?.triggerFiltersEvents?.[0]?.conditions[0].eventName;
    const isSegmentUpload = eventType === NodeTriggerType.AddContactToStaticSegment;
    const validScheduledTrigger = eventType === NodeTriggerType.Scheduled && node.data?.scheduledJourneyStartDate;
    const validEventTrigger = eventName && eventType;

    return isSegmentUpload || validScheduledTrigger || validEventTrigger;
}

function validateDelay(node: FlowElement): boolean {
    const {
        days,
        hours,
        minutes,
        seconds,
        skipTimeOfDay,
        skipDaysOfWeek,
        skipFromTime,
        skipToTime,
        selectedWeekDays,
        delayType,
        delayPropertyName,
        contactAttribute,
    } = node.data;

    return (
        !!days ||
        !!hours ||
        !!minutes ||
        !!seconds ||
        (skipTimeOfDay && (!!skipFromTime || !!skipToTime)) ||
        (skipDaysOfWeek && !!selectedWeekDays) ||
        (delayType === "property" && (!!delayPropertyName || !!contactAttribute))
    );
}

function validateWebHookNode(node: FlowElement): boolean {
    try {
        const { webhookName, webhookUrl, webhookMethod } = node.data;
        const isValid = isValidString(webhookUrl) && isValidString(webhookName) && isValidString(webhookMethod);
        // below should validate if the url is in the right format.
        new URL(webhookUrl);
        return isValid;
    } catch {
        return false;
    }
}

function isValidString(str: string) {
    if (str === null || str === undefined) {
        return false;
    }
    const trimmedStr = str.trim();
    return trimmedStr.length > 0;
}

function validateConditional(node: FlowElement): boolean {
    // TODO; tests
    const {
        complexConditional,
        conditionalType,
        eventName,
        propertyName,
        propertyOperator,
        propertyValue,
        relativeDateValue,
        contactAttribute,
    } = node.data || {};

    if (complexConditional) {
        return true;
    }

    // TODO: keep the v1 validation for now
    // once v2 is in prod confirmed working remove it
    const validProperty =
        (propertyName && !operatorRequiresPropertyValue(propertyOperator)) ||
        (!propertyName && !propertyValue) ||
        (propertyName && propertyValue);

    // Check that attribute or event name are provided
    const baseSchemaCheck =
        (conditionalType === "contactAttribute" && !!contactAttribute) ||
        (conditionalType === "triggeringEvent" && !!propertyName) ||
        !!eventName;

    const validRelativeDate = !propertyValue && !!relativeDateValue;
    const validContactComparison = !!contactAttribute && !!propertyValue;
    const validTriggerComparison = contactAttribute === "triggeringEvent" && !!propertyName;
    const validComparison = validProperty || validRelativeDate || validContactComparison || validTriggerComparison;
    return conditionalType && baseSchemaCheck && validComparison;
}

export function triggerLiveAndDateInPast(
    state: string,
    data: {
        id?: string;
        name: string;
        state: string;
        flow: Elements<any>;
        version: number;
        usedNodeCount: number;
        description?: string;
    }
): boolean {
    if (
        state == JourneyStatus.LIVE &&
        new Date(data.flow.find((elt) => elt.type == "trigger")?.data.scheduledJourneyStartDate) < new Date()
    ) {
        return true;
    }
    return false;
}
